import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { QUERY_KEYS, useGetCheckpointServiceAxiosInstance, useGetTransportAxiosInstance } from "@/constants";
import useAuth from "@/providers/hooks/useAuth";
import { commonListResponseSchema } from "@/schemas/commonList";
import {
    IHotelPlanningDepartureCustomItem,
    IHotelPlanningDepartureResponse,
    hotelDepartureCustomResponseSchema,
    hotelDeparturePassengerTypeSchema,
    hotelPlanningDepartureResponseSchema,
} from "@/schemas/hotelPlanningDepartureSchema";
import { isHotelDeparturePastDate, toHotelDepartureIsoDate } from "@/stores/use-hotel-planning-departure-table";

/**
 * Checkpoint action types for entering or exiting a checkpoint
 */
export enum CheckpointActionType {
    /** Enter/start a checkpoint */
    ENTER = "enter",
    /** Exit/complete a checkpoint */
    EXIT = "exit",
}

/** Past days don't change anymore — no focus refetch, long cache. Today keeps the live settings. */
const getHotelDepartureCacheOptions = (date: DateTime) =>
    isHotelDeparturePastDate(date)
        ? { staleTime: 1000 * 60 * 30, refetchOnWindowFocus: false }
        : { staleTime: 1000 * 60 * 2 };

/**
 * `?date=` today (or omitted) → live pool (waiting / in transit / arrived in the last 120 min);
 * past day → that day's TRANSFER_GO records (history, `waitingTime` always null).
 */
export const useGetHotelDeparturePlanning = (date: DateTime) => {
    const { auth } = useAuth();
    const axiosInstance = useGetCheckpointServiceAxiosInstance();
    const isoDate = toHotelDepartureIsoDate(date);
    return useQuery<IHotelPlanningDepartureResponse>({
        enabled: !!auth?.accessToken && !!isoDate,
        queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_PLANNING, isoDate],
        retryOnMount: false,
        ...getHotelDepartureCacheOptions(date),
        retry: 2,
        queryFn: async () => {
            const res = await axiosInstance.get<IHotelPlanningDepartureResponse>(`/patient/hotel/departure`, {
                params: { date: isoDate },
            });
            if (!res.data) throw new Error("No data found");

            const tagged = res.data.map((item) => ({ ...item, type: hotelDeparturePassengerTypeSchema.enum.clinic }));
            const list = hotelPlanningDepartureResponseSchema.safeParse(tagged);
            if (!list.success) {
                console.error(list.error);
                // Keep the "clinic" tag even on a parse mismatch — losing it breaks type discrimination
                // downstream (e.g. routes edits on these rows to the wrong backend service).
                return tagged as IHotelPlanningDepartureResponse;
            }

            return list.data;
        },
    });
};

/**
 * Custom (transport-request) clinic→hotel passengers. Served by transport-svc, date in the URL path.
 * Returned items are display-only in the table for now; the modifications endpoint is not wired yet.
 */
export const useGetHotelDepartureCustomPassengers = (date: DateTime) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();
    const isoDate = toHotelDepartureIsoDate(date);

    return useQuery<IHotelPlanningDepartureCustomItem[]>({
        enabled: !!auth?.accessToken && !!isoDate,
        queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_CUSTOM_PASSENGERS, isoDate],
        retryOnMount: false,
        refetchOnWindowFocus: false,
        ...getHotelDepartureCacheOptions(date),
        retry: 2,
        queryFn: async () => {
            const { data } = await axiosInstance.get<unknown>(`/clinic-hotel/passengers/${isoDate}`);

            const parsed = hotelDepartureCustomResponseSchema.safeParse(data);
            if (!parsed.success) {
                console.error(parsed.error);
                return (data ?? []) as IHotelPlanningDepartureCustomItem[];
            }

            return parsed.data;
        },
    });
};

export type HotelDepartureChangesPayloadItem = {
    operationId: string;
    assignedUserId?: string | null;
    uniqueCheckpointId: string;
    labelledUserId?: string | null;
};
export const useSaveHotelDepartureChanges = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetCheckpointServiceAxiosInstance();

    return useMutation<
        { id: string; ok: boolean; message: string }[],
        Error,
        {
            operations: HotelDepartureChangesPayloadItem[];
        }
    >({
        mutationFn: async ({ operations }) => {
            const res = await axiosInstance
                .patch(`/patient/hotel/departure/bulk`, { operations })
                .then((res) => res.data);
            const parsedData = commonListResponseSchema.safeParse(res);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_PLANNING],
            });

            return res;
        },
    });
};

export type HotelDepartureProgressPayloadItem = {
    operationId: string;

    uniqueCheckpointId: string;

    action: CheckpointActionType;
};

export const useHotelDepartureProgressMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetCheckpointServiceAxiosInstance();

    return useMutation<
        { id: string; ok: boolean; message: string }[],
        Error,
        {
            patients: HotelDepartureProgressPayloadItem[];
        }
    >({
        mutationFn: async ({ patients }) => {
            const res = await axiosInstance.patch(`/patients/progress/bulk`, { patients }).then((res) => res.data);
            const parsedData = commonListResponseSchema.safeParse(res);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_PLANNING],
            });

            return res;
        },
    });
};
